import React, { useEffect, useMemo } from 'react';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import {
	createRecordPathCodec,
	IframeEmbedParams,
	QuickbaseIFrameContainer,
	quickbaseTabStoreSelector,
	recordPathSegmentFor,
	recordPresentationFor,
	TabType,
	useNxOrdersStore,
	useQuickbaseHostBridgeSync,
} from '@warehouse/quickbase';

export function NxOrders() {
	const title = useTitleEditorStore(titleEditorStoreSelector.title);

	// Store selectors
	const isLoading = useNxOrdersStore(quickbaseTabStoreSelector.isLoading);
	const isAuthenticated = useNxOrdersStore(quickbaseTabStoreSelector.isAuthenticated);
	const error = useNxOrdersStore(quickbaseTabStoreSelector.error);
	const embedUrl = useNxOrdersStore(quickbaseTabStoreSelector.embedUrl);
	const frameUrl = useNxOrdersStore(quickbaseTabStoreSelector.frameUrl);
	const showAuthOverlay = useNxOrdersStore(quickbaseTabStoreSelector.showAuthOverlay);
	const authFlow = useNxOrdersStore(quickbaseTabStoreSelector.authFlow);
	const actions = useNxOrdersStore(quickbaseTabStoreSelector.actions);

	const { authenticate, loadEmbedUrl, setShowAuthOverlay, dismissAuthOverlay, recoverSession } = actions;

	const codec = useMemo(
		() =>
			createRecordPathCodec({
				resolveBasePath: (pathname) => pathname.match(/^\/title\/[^/]+/)?.[0] ?? null,
				recordSegment: recordPathSegmentFor(TabType.NxOrders),
			}),
		[],
	);

	const { bridge, initialEmbedParams, reloadToken } = useQuickbaseHostBridgeSync({
		embedUrl,
		codec,
		reloadEmbedUrl: (embedParams: IframeEmbedParams) => {
			if (title?.uuid) loadEmbedUrl(title.uuid, embedParams);
		},
		presentation: recordPresentationFor(TabType.NxOrders),
	});

	// Load embed URL when title changes
	useEffect(() => {
		if (title?.uuid) {
			loadEmbedUrl(title.uuid, initialEmbedParams);
		}
	}, [title?.uuid, loadEmbedUrl, initialEmbedParams]);

	return (
		<QuickbaseIFrameContainer
			isLoading={isLoading}
			isAuthenticated={isAuthenticated}
			error={error}
			embedUrl={embedUrl}
			frameUrl={frameUrl}
			showAuthOverlay={showAuthOverlay}
			authenticate={authenticate}
			setShowAuthOverlay={setShowAuthOverlay}
			dismissAuthOverlay={dismissAuthOverlay}
			recoverSession={recoverSession}
			authFlow={authFlow}
			iFrameType="NxOrders"
			bridge={bridge}
			reloadToken={reloadToken}
		/>
	);
}
